import clubRequest from "@/services/axios";
import { SettlementStatus as ApiSettlementStatus } from "@/models/club";

export type SettlementStatus = 0 | 1;

export interface PayableSummary {
  merchantUserId: number;
  merchantUserName?: string | null;
  itemCount: number;
  totalAmount: number;
}

export interface PayableItem {
  customerPurchaseItemId: number;
  customerPurchaseId: number;
  customerName?: string | null;
  serviceName?: string | null;
  purchaseDate?: string;
  grossAmount: number;
  commissionAmount: number;
  amount: number;
}

export interface PageResponse<T> {
  count: number;
  result: T[];
  totalAmount?: number;
}

export interface SettlementRecord {
  id: number;
  merchantUserId: number;
  merchantUserName?: string | null;
  fromDate: string;
  toDate: string;
  settleDate: string;
  rrn?: string | null;
  totalAmount: number;
  itemCount: number;
  status: SettlementStatus;
}

export interface CreateSettlementPayload {
  merchantUserId: number;
  fromDate: string;
  toDate: string;
  customerPurchaseItemIds: number[];
  settleDate: string;
  rrn?: string;
  description?: string;
}

const settlementService = {
  getPayableSummary: (
    fromDate?: string,
    toDate?: string,
    merchantUserId?: number
  ) =>
    clubRequest
      .merchantUserSettlementPayableSummaryList({
        FromDate: fromDate,
        ToDate: toDate,
        MerchantUserId: merchantUserId,
        Skip: 0,
        Count: 200,
      })
      .then((response): PayableSummary[] =>
        response.data
          .filter((item) => item.merchantUserId != null)
          .map((item) => ({
            merchantUserId: item.merchantUserId!,
            merchantUserName: item.merchantUserFullName,
            itemCount: item.itemCount ?? 0,
            totalAmount: item.payableAmount ?? 0,
          }))
      ),
  getPayableItems: (params: {
    merchantUserId: number;
    fromDate?: string;
    toDate?: string;
    skip?: number;
    count?: number;
  }) =>
    clubRequest
      .merchantUserSettlementPayableList({
        MerchantUserId: params.merchantUserId,
        FromDate: params.fromDate,
        ToDate: params.toDate,
        Skip: params.skip,
        Count: params.count,
      })
      .then((response): PageResponse<PayableItem> => ({
        count: response.data.count ?? 0,
        result: (response.data.result ?? [])
          .filter((item) => item.customerPurchaseItemId != null)
          .map((item) => ({
            customerPurchaseItemId: item.customerPurchaseItemId!,
            customerPurchaseId: item.customerPurchaseId ?? 0,
            customerName: item.customerFullName,
            serviceName: item.serviceTitle,
            purchaseDate: item.purchaseDate,
            grossAmount: item.grossAmount ?? 0,
            commissionAmount: item.commissionAmount ?? 0,
            amount: item.payableAmount ?? 0,
          })),
      })),
  create: (payload: CreateSettlementPayload) =>
    clubRequest.merchantUserSettlementCreate(payload),
  getHistory: (params: { skip?: number; count?: number }) =>
    clubRequest
      .merchantUserSettlementList({ Skip: params.skip, Count: params.count })
      .then((response): PageResponse<SettlementRecord> => ({
        count: response.data.count ?? 0,
        result: (response.data.result ?? [])
          .filter((item) => item.id != null && item.merchantUserId != null)
          .map((item) => ({
            id: item.id!,
            merchantUserId: item.merchantUserId!,
            merchantUserName: item.merchantUserFullName,
            fromDate: item.fromDate ?? "",
            toDate: item.toDate ?? "",
            settleDate: item.settleDate ?? "",
            rrn: item.rrn,
            totalAmount: item.totalAmount ?? 0,
            itemCount: item.itemCount ?? 0,
            status:
              item.status === ApiSettlementStatus.Value1 ? 1 : 0,
          })),
      })),
  getDetail: (settlementId: number) =>
    clubRequest.merchantUserSettlementDetail({ settlementId }),
  cancel: ({ id, reason }: { id: number; reason?: string }) =>
    clubRequest.merchantUserSettlementCancelCreate(
      { settlementId: id },
      { reason: reason || null }
    ),
};

export default settlementService;
